import axios from "axios"
import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react"
import {API_URL} from "../constants"
import {AcapulcoCondo, BaseCondo, PropertyType, RegionCode} from "@casas-diamante/types"
import {CondoPropertiesSortBy} from "@/types/Condo"
import {PropertyResponse} from "@/types/Property"

// Set to use the credentials for AUTH
axios.defaults.withCredentials = true
const URL_PREFIX = `${API_URL}/condos`

type CondoPriceComparison = {
   averagePricePerMeter: number | null
   propertyCountUsed: number
   currentPropertyPricePerMeter: number | null
   percentDifference: number | null
   direction: "below" | "above" | "equal" | null
   status: "neutral" | "belowAverage" | "wellBelowAverage" | "aboveAverage"
}

type CondoCategoryComparison = {
   percentDifference: number
   direction: "below" | "above" | "equal"
}

export type CondoPriceMetricsResponse = {
   averagePricePerMeter: number | null
   averagePropertyCount: number
   comparison: CondoPriceComparison | null
   categoryComparison: CondoCategoryComparison | null
   averageMessage: string | null
   propertyTypeLabel: string
}

export type CondoTypeAverageSummary = {
   propertyType: PropertyType
   propertyTypeLabel: string
   averagePricePerMeter: number
   propertyCountUsed: number
}

export type CondoPriceMetricsByTypeResponse = {
   summaries: CondoTypeAverageSummary[]
   categorySummaries: CondoTypeAverageSummary[]
}

export type CondoPriceAnalysisRow = {
   propertyType: PropertyType
   label: string
   condoPricePerMeter: number | null
   categoryPricePerMeter: number | null
   comparison: {
      percentDifference: number
      direction: "below" | "above" | "equal"
   } | null
   condoPropertyCount: number | null
   categoryPropertyCount: number | null
}

export type CondoPriceAnalysisResponse = {
   condoName: string | null
   categoryLabel: string | null
   rows: CondoPriceAnalysisRow[]
   generatedAt: string
}

export type CondoPropertiesPageArgs = {
   condoId: string
   pageSize: number
   startAfter?: string
   sortBy?: CondoPropertiesSortBy
}

export type CondoPropertiesPageResponse = {
   data: PropertyResponse[]
   totalCount: number
}

export const condosRtkApi = createApi({
   reducerPath: "condosApi",
   baseQuery: fetchBaseQuery({
      baseUrl: URL_PREFIX,
      credentials: "include",
   }),
   tagTypes: ["CondoPriceAnalysis"],
   keepUnusedDataFor: 300,
   refetchOnMountOrArgChange: 300,
   endpoints: (builder) => ({
      getCondoPropertiesPage: builder.query<CondoPropertiesPageResponse, CondoPropertiesPageArgs>({
         query: ({condoId, pageSize, startAfter = "", sortBy}) => {
            const params = new URLSearchParams({
               pageSize: String(pageSize),
               startAfter,
            })

            if (sortBy) params.set("sortBy", sortBy)

            return {
               url: `/${condoId}/properties?${params.toString()}`,
            }
         },
      }),
      getCondoPriceAnalysis: builder.query<CondoPriceAnalysisResponse, string>({
         query: (condoId) => `/${condoId}/price-analysis`,
         providesTags: (_result, _error, condoId) => [
            {type: "CondoPriceAnalysis", id: condoId},
            {type: "CondoPriceAnalysis", id: "LIST"},
         ],
      }),
   }),
})

export const {useLazyGetCondoPropertiesPageQuery, useGetCondoPriceAnalysisQuery} = condosRtkApi

export const getCondos = async (
   region: RegionCode,
   lastCond: string | null,
   q: string,
   page: number,
   classification?: string,
) => {
   const classParam = classification ? `&class=${classification}` : ""
   if (!lastCond || q !== "") {
      return await axios.get(
         `${URL_PREFIX}?region=${region}&q=${q}&searchPage=${page}${classParam}`,
      )
   }
   return await axios.get(`${URL_PREFIX}?region=${region}&lastCondominio=${lastCond}${classParam}`)
}

export const getCondoById = async (id: string) =>
   (await axios.get(`${URL_PREFIX}/${id}?by=id`)).data

export const getAllCondos = async () => (await axios.get(`${API_URL}/condos/all`)).data

export const getCondoLinks = async (region: RegionCode) =>
   (await axios.get(`${URL_PREFIX}/links?region=${region}`)).data

export const getCondoProperties = async (condId: string) => {
   const res = await axios.get(`${URL_PREFIX}/${condId}/properties`)
   const data = res.data
   if (Array.isArray(data)) return data
   if (Array.isArray(data?.properties)) return data.properties
   if (Array.isArray(data?.data)) return data.data
   return []
}

export const getCondoPriceMetrics = async (condId: string, propertyId: string) =>
   (
      await axios.get(
         `${URL_PREFIX}/${condId}/price-metrics?propertyId=${encodeURIComponent(propertyId)}`,
      )
   ).data as CondoPriceMetricsResponse

export const getCondoPriceMetricsByType = async (condId: string) => {
   const res = await axios.get(`${URL_PREFIX}/${condId}/price-metrics/by-type`)
   return {
      summaries: res.data?.summaries ?? [],
      categorySummaries: res.data?.categorySummaries ?? [],
   } as CondoPriceMetricsByTypeResponse
}

export const postCondo = async (condo: AcapulcoCondo | BaseCondo) => {
   const res = await axios({
      method: "post",
      url: URL_PREFIX,
      data: condo,
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data
}

export const putCondoImages = async (FormData: any, id: string) => {
   const resPut = await axios({
      method: "PUT",
      url: `${URL_PREFIX}/${id}/images`,
      data: FormData,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
   return resPut
}

export const updateCondo = async (updatedCondo: AcapulcoCondo | BaseCondo, id: string) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/${id}`,
      data: updatedCondo,
      headers: {
         "Content-Type": "application/json",
      },
   })
}

export const orderImages = async (id: string, images: any, condominio: any) => {
   await axios({
      method: "PUT",
      url: `${URL_PREFIX}/condominio/orderImages`,
      data: {id: id, images: images, condominio: condominio},
      headers: {
         "Content-Type": "application/json",
      },
   })
}

export const orderSections = async (id: string, images: any, categoriesUpdated: any) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/updateSections`,
      data: {
         id,
         orderedSections: categoriesUpdated,
         images,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
}

export const updateMainImage = async (id: string, formData: FormData) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/${id}/mainImage`,
      data: formData,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
}

export const addImages = async (id: string, formData: FormData) => {
   await axios({
      method: "put",
      url: `${URL_PREFIX}/addImages/${id}`,
      data: formData,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
}

export const deleteImages = async (id: string, deletedImages: string[]) => {
   await axios({
      method: "delete",
      url: `${URL_PREFIX}/deleteImages/${id}`,
      data: {
         deletedImages,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
}

export const deleteSectionImages = async (
   id: string,
   categories: any,
   categoriesUpdated: any,
   images: any,
) => {
   const res = await axios({
      method: "delete",
      url: `${URL_PREFIX}/categoryImages`,
      data: {
         id,
         categories,
         categoriesUpdated,
         images,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
   return res.data.images
}

export const deleteMainImage = async (id: string, email: string, mainImage: string) => {
   await axios({
      method: "delete",
      url: `${URL_PREFIX}/${id}/mainImage`,
      data: {
         mainImage,
      },
      headers: {
         "Content-Type": "application/json",
      },
   })
}

export const uploadNewImages = async (id: string, email: string, images: any) => {
   const res = await axios({
      method: "put",
      url: `${URL_PREFIX}/uploadNewImages/${id}`,
      data: images,
      headers: {
         "Content-Type": "multipart/form-data; charset=utf-8",
      },
   })
   return res.data.filePaths
}
